import axios from "axios"
import { getApiUrl } from "@/services/env.helper"
import appVersion from "@/version.json"

// Minimal self-hosted error tracking: browser errors are sent to the backend (POST /frontend/errors),
// which forwards them to the "vueError" telegram bot. Sending is fire-and-forget (fetch with keepalive,
// no awaiting, no axios interceptors), so it never blocks or slows down the user.

const MAX_REPORTS_PER_PAGE_LOAD = 10

// noise that is not actionable: browser quirks, extensions, cross-origin scripts, stale chunks after a deploy
const IGNORED_MESSAGES = [
	'ResizeObserver loop',
	'Script error.',
	'Loading chunk',
	'Loading CSS chunk',
	'Failed to fetch dynamically imported module',
	'Importing a module script failed',
	'NavigationDuplicated',
	'Avoided redundant navigation',
	'Navigation cancelled',
	'Redirected when going from',
	'Ya is not defined', // yandex metrika script blocked by the browser / an ad blocker (services/metrika.js)
	'Load failed', // safari / chrome / firefox network failures of fetch requests - no response, nothing to fix in code
	'Failed to fetch',
	'NetworkError when attempting to fetch resource',
	'from database without an in-progress transaction', // safari indexedDB bug (delete range / get a record)
	// indexedDB of the browser (used by the firebase messaging sdk): full disk, profile cleaned up by the user
	'QuotaExceededError',
	'FILE_ERROR_NO_SPACE',
	'Database deleted by request of the user',
	'Unable to open database file on disk',
	'Encountered disk full while committing transaction',
	'The transaction was aborted, so the request cannot be fulfilled', // follows the disk full error above
	'AbortError', // a request or operation cancelled on purpose (navigation, AbortController)
	'The operation was aborted.',
	'[object Event]', // promise rejected with a DOM event (e.g. a failed image/script load) - no message or stack
	'installations/app-offline', // firebase messaging sdk without network
	'contentScriptData', // a browser extension content script
]
const IGNORED_SOURCES = [
	'chrome-extension://', 'moz-extension://', 'safari-extension://',
	'FormMetadata.js', // safari / ios password autofill
	'_getOwnPropertyDescriptor', // a script injected into the page (extension), recursing in its own wrapper
]

const reported = new Set()
let reportsCount = 0
let store = null

// a stale page file of the previous build: the server returned index.html instead of the js (see router.onError)
const isStaleChunk = (message, source) => message.startsWith("Unexpected token '<'") && /\/js\/[^/]+\.js:1:[01]$/.test(source || '')

const shouldIgnore = (message, stack, source) => {
	if(!message) return true
	if(isStaleChunk(message, source)) return true
	if(IGNORED_MESSAGES.some(m => message.includes(m))) return true
	return IGNORED_SOURCES.some(s => (stack || '').includes(s) || (source || '').includes(s))
}

const reportError = (error, extra = {}) => {
	try {
		// api errors are already logged on the backend, and failed requests are expected (validation, 401...)
		if(error?.isAxiosError) return
		const message = String(error?.message || error || '').slice(0, 1000)
		const stack = String(error?.stack || '').slice(0, 4000)
		if(shouldIgnore(message, stack, extra.source)) return

		const fingerprint = `${message}|${extra.source || ''}`
		if(reported.has(fingerprint) || reportsCount >= MAX_REPORTS_PER_PAGE_LOAD) return
		reported.add(fingerprint)
		reportsCount++

		const baseUrl = (axios.defaults.baseURL || getApiUrl()).replace(/\/$/, '')
		fetch(`${baseUrl}/frontend/errors`, {
			method: 'POST',
			keepalive: true,
			headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
			body: JSON.stringify({
				message,
				version: appVersion.version, // the build that produced the error (stacks map to its chunks)
				stack,
				source: extra.source || null,
				info: extra.info || null,
				component: extra.component || null,
				url: window.location.href,
				userAgent: navigator.userAgent,
				userId: store?.state?.auth?.user?.id || null,
				clinicId: store?.state?.auth?.clinic?.id || null,
			}),
		}).catch(() => {}) // reporting must never produce new errors
	} catch(e) {
		// reporting must never produce new errors
	}
}

const installErrorReporter = (app, vuexStore) => {
	if(process.env.NODE_ENV !== 'production') return
	store = vuexStore

	// errors inside vue components (render, lifecycle hooks, watchers, event handlers)
	const previousHandler = app.config.errorHandler
	app.config.errorHandler = (err, instance, info) => {
		reportError(err, {
			info,
			component: instance?.$options?.name || instance?.$options?.__name || null,
		})
		if(previousHandler) {
			previousHandler(err, instance, info)
		} else {
			console.error(err) // setting errorHandler disables vue's own console output
		}
	}

	// errors outside of vue (plain scripts, timers, third-party code)
	window.addEventListener('error', (event) => {
		if(!event.error && !event.message) return // failed <img>/<script> loads - not code errors
		reportError(event.error || event.message, {
			source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : null,
		})
	})

	// promises rejected without a .catch()
	window.addEventListener('unhandledrejection', (event) => {
		reportError(event.reason, { info: 'unhandled promise rejection' })
	})
}

export {
	installErrorReporter,
	reportError
}
